import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Hd, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { api } from "../../../../api/client";
import type { UpscaleTargetResolution } from "../../../../api/types";
import { useActiveCanvasId } from "../../../../contexts/ActiveCanvasIdContext";
import { useProjectId } from "../../../../contexts/ProjectIdContext";
import { useReadOnly } from "../../../../contexts/ReadOnlyContext";
import { useBooleanFlagValue } from "../../../../hooks/useBooleanFlagValue";
import { createNadDebug } from "../../../../utils/debugLog";
import type { ToolbarAction } from "./AgentNodeToolbar";
import { FlyoutPortal } from "./FlyoutPortal";

const upscaleVideoLog = createNadDebug("dw:upscale-video:client");
const videoToolbarReferenceLog = createNadDebug(
  "dw:video-node-toolbar-reference",
  { priority: true },
);

const UPSCALE_TARGETS: readonly {
  pixels: number;
  resolution: UpscaleTargetResolution;
}[] = [
  { pixels: 480, resolution: "480p" },
  { pixels: 720, resolution: "720p" },
  { pixels: 1080, resolution: "1080p" },
];
const MAX_SCALE_FACTOR = 4;
const CANVAS_ALIGNMENT = 128;
const MAX_PADDED_EDGE = 2048;
const MAX_PADDED_PIXELS = 2_359_296;

export interface VideoDimensions {
  width: number;
  height: number;
}

export function videoDimensionsFromMetadata(
  metadata:
    | { actual_width?: number; actual_height?: number }
    | null
    | undefined,
): VideoDimensions | null {
  const width = metadata?.actual_width;
  const height = metadata?.actual_height;
  if (
    typeof width !== "number" ||
    typeof height !== "number" ||
    !Number.isSafeInteger(width) ||
    !Number.isSafeInteger(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return { width, height };
}

function roundRatioToNearestEven(numerator: number, denominator: number): number {
  return 2 * Math.floor((numerator + denominator) / (2 * denominator));
}

function paddedDimension(value: number): number {
  return Math.ceil(value / CANVAS_ALIGNMENT) * CANVAS_ALIGNMENT;
}

/**
 * Return the target short-edge tiers accepted by the current FlashVSR serving
 * budget. The backend repeats these checks authoritatively before scheduling.
 */
export function getEligibleUpscaleTargetResolutions(
  dimensions: VideoDimensions | null | undefined,
): UpscaleTargetResolution[] {
  if (!dimensions) return [];
  const { width, height } = dimensions;
  if (
    !Number.isSafeInteger(width) ||
    !Number.isSafeInteger(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return [];
  }

  const shortEdge = Math.min(width, height);
  const longEdge = Math.max(width, height);
  return UPSCALE_TARGETS.filter(({ pixels: target }) => {
    if (target <= shortEdge || target / shortEdge > MAX_SCALE_FACTOR) return false;
    const outputLongEdge = roundRatioToNearestEven(
      longEdge * target,
      shortEdge,
    );
    const paddedShortEdge = paddedDimension(target);
    const paddedLongEdge = paddedDimension(outputLongEdge);
    return (
      paddedShortEdge <= MAX_PADDED_EDGE &&
      paddedLongEdge <= MAX_PADDED_EDGE &&
      paddedShortEdge * paddedLongEdge <= MAX_PADDED_PIXELS
    );
  }).map(({ resolution }) => resolution);
}

/**
 * Why the HD action is greyed out. ``null`` means it is usable (or the reason
 * is self-evident, e.g. no generated video yet / the source is busy).
 */
export type UpscaleUnavailableReason =
  | "checking-resolution"
  | "resolution-unknown"
  | "max-resolution"
  | "unsupported-size";

const MAX_TARGET_SHORT_EDGE = UPSCALE_TARGETS[UPSCALE_TARGETS.length - 1].pixels;

/**
 * Classify why a video with a playable output has no eligible HD target.
 * Pure so tests (and the demo) can pin every branch.
 */
export function getUpscaleUnavailableReason({
  dimensions,
  probeFailed,
  canProbe,
}: {
  dimensions: VideoDimensions | null;
  probeFailed: boolean;
  canProbe: boolean;
}): UpscaleUnavailableReason | null {
  if (!dimensions) {
    return probeFailed || !canProbe ? "resolution-unknown" : "checking-resolution";
  }
  if (getEligibleUpscaleTargetResolutions(dimensions).length > 0) return null;
  return Math.min(dimensions.width, dimensions.height) >= MAX_TARGET_SHORT_EDGE
    ? "max-resolution"
    : "unsupported-size";
}

const UNAVAILABLE_REASON_I18N_KEY: Record<UpscaleUnavailableReason, string> = {
  "checking-resolution": "upscaleVideo.unavailable.checkingResolution",
  "resolution-unknown": "upscaleVideo.unavailable.resolutionUnknown",
  "max-resolution": "upscaleVideo.unavailable.maxResolution",
  "unsupported-size": "upscaleVideo.unavailable.unsupportedSize",
};

function HdGlyph() {
  return (
    <span
      aria-hidden="true"
      className="text-xxs font-bold leading-none tracking-tight"
    >
      HD
    </span>
  );
}

interface UpscaleVideoFlyoutProps {
  eligibleTargets: readonly UpscaleTargetResolution[];
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onTargetChange: (target: UpscaleTargetResolution) => void;
  selectedTarget: UpscaleTargetResolution;
  titleId: string;
}

function UpscaleVideoFlyout({
  eligibleTargets,
  isSubmitting,
  onCancel,
  onConfirm,
  onTargetChange,
  selectedTarget,
  titleId,
}: UpscaleVideoFlyoutProps) {
  const { t } = useTranslation("canvas");
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // FlyoutPortal makes itself visible on the next animation frame. Wait one
    // additional frame so focus is not discarded while the portal is hidden.
    let focusFrame: number | undefined;
    const visibilityFrame = window.requestAnimationFrame(() => {
      focusFrame = window.requestAnimationFrame(() => {
        dialogRef.current?.focus();
      });
    });
    return () => {
      window.cancelAnimationFrame(visibilityFrame);
      if (focusFrame !== undefined) window.cancelAnimationFrame(focusFrame);
    };
  }, []);

  return (
    <div
      ref={dialogRef}
      id={`${titleId}-dialog`}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      tabIndex={-1}
      className="w-72 focus:outline-none"
      data-testid="upscale-video-flyout"
    >
      <h2 id={titleId} className="text-sm font-semibold text-foreground">
        {t("upscaleVideo.configureTitle")}
      </h2>
      <p className="mt-1 text-xs font-medium text-muted-foreground">
        {t("upscaleVideo.configureDescription")}
      </p>
      <fieldset className="mt-4">
        <legend className="text-xs font-medium text-foreground">
          {t("upscaleVideo.resolutionLabel")}
        </legend>
        <div
          className="mt-1.5 flex gap-2"
          data-testid="upscale-video-resolution"
        >
          {eligibleTargets.map((target) => (
            <label key={target} className="min-w-0 flex-1 cursor-pointer">
              <input
                type="radio"
                name={`${titleId}-target-resolution`}
                value={target}
                checked={selectedTarget === target}
                disabled={isSubmitting}
                onChange={() => onTargetChange(target)}
                className="peer sr-only"
              />
              <span
                className={`flex h-9 items-center justify-center rounded-lg border text-sm font-medium transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background peer-disabled:cursor-not-allowed peer-disabled:opacity-40 ${
                  selectedTarget === target
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-border bg-surface-1 text-muted-foreground hover:bg-surface-2 hover:text-foreground"
                }`}
              >
                {target}
              </span>
            </label>
          ))}
        </div>
        <p className="mt-2 text-xxs leading-relaxed text-muted-foreground">
          {t("upscaleVideo.limitsDescription")}
        </p>
      </fieldset>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-40"
        >
          {t("upscaleVideo.cancel")}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={isSubmitting}
          className="flex h-9 items-center gap-2 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-40"
        >
          {isSubmitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : null}
          {t("upscaleVideo.generateHd")}
        </button>
      </div>
    </div>
  );
}

export interface UpscaleVideoActionResult {
  action: ToolbarAction;
  flyout: ReactNode;
}

/** Build the HD toolbar action and its configuration flyout. */
export function useUpscaleVideoAction({
  nodeId,
  hasVideo,
  hdIconVariant = "text",
  hideTerminallyUnavailable = false,
  isEligible = true,
  isBusy = false,
  sourceName,
  sourceDimensions,
  sourceVideoPath,
}: {
  nodeId: string;
  hasVideo: boolean;
  /** Use the outlined reference badge only on the two video-node docks. */
  hdIconVariant?: "text" | "badge";
  /** Video-node reference docks omit actions that cannot become available. */
  hideTerminallyUnavailable?: boolean;
  isEligible?: boolean;
  isBusy?: boolean;
  sourceName?: string;
  sourceDimensions?: VideoDimensions | null;
  sourceVideoPath?: string | null;
}): UpscaleVideoActionResult {
  const { t } = useTranslation("canvas");
  const projectId = useProjectId();
  const canvasId = useActiveCanvasId();
  const readOnly = useReadOnly();
  const flagEnabled = useBooleanFlagValue("upscale_video_enabled", false);
  const suppliedWidth = sourceDimensions?.width;
  const suppliedHeight = sourceDimensions?.height;
  const suppliedDimensions = useMemo(
    () =>
      videoDimensionsFromMetadata({
        actual_width: suppliedWidth,
        actual_height: suppliedHeight,
      }),
    [suppliedHeight, suppliedWidth],
  );
  const [probedDimensions, setProbedDimensions] = useState<{
    path: string;
    dimensions: VideoDimensions;
  } | null>(null);
  const [probeFailedPath, setProbeFailedPath] = useState<string | null>(null);
  const effectiveDimensions =
    suppliedDimensions ??
    (probedDimensions && probedDimensions.path === sourceVideoPath
      ? probedDimensions.dimensions
      : null);
  const sourceWidth = effectiveDimensions?.width;
  const sourceHeight = effectiveDimensions?.height;
  const eligibleTargets = useMemo(
    () =>
      getEligibleUpscaleTargetResolutions(
        sourceWidth === undefined || sourceHeight === undefined
          ? null
          : { width: sourceWidth, height: sourceHeight },
      ),
    [sourceHeight, sourceWidth],
  );
  const defaultTarget = eligibleTargets[eligibleTargets.length - 1];
  const featurePreEligible = !readOnly && isEligible && flagEnabled;
  // A playable video with no eligible target stays VISIBLE but greyed out
  // with a reason (Workplace 1632037181886912) — hiding it made the user
  // think HD was missing rather than "already at maximum resolution".
  const available = featurePreEligible;
  const unavailableReason: UpscaleUnavailableReason | null =
    featurePreEligible && hasVideo
      ? getUpscaleUnavailableReason({
          dimensions: effectiveDimensions,
          probeFailed:
            sourceVideoPath !== undefined &&
            sourceVideoPath !== null &&
            probeFailedPath === sourceVideoPath,
          canProbe: Boolean(projectId && sourceVideoPath),
        })
      : null;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const terminallyUnavailable =
    unavailableReason !== null && unavailableReason !== "checking-resolution";
  const hideAction =
    !available ||
    (hideTerminallyUnavailable &&
      !isBusy &&
      !isSubmitting &&
      (!hasVideo || terminallyUnavailable));
  const triggerRef = useRef<HTMLButtonElement>(null);
  const shouldRestoreFocusRef = useRef(false);
  const titleId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] =
    useState<UpscaleTargetResolution | null>(defaultTarget ?? null);

  useEffect(() => {
    if (
      !featurePreEligible ||
      suppliedDimensions ||
      !projectId ||
      !hasVideo ||
      !sourceVideoPath
    ) {
      return;
    }

    const video = document.createElement("video");
    const handleLoadedMetadata = () => {
      const dimensions = videoDimensionsFromMetadata({
        actual_width: video.videoWidth,
        actual_height: video.videoHeight,
      });
      if (dimensions) {
        setProbedDimensions({ path: sourceVideoPath, dimensions });
      } else {
        setProbeFailedPath(sourceVideoPath);
      }
    };
    const handleError = () => {
      setProbeFailedPath(sourceVideoPath);
    };
    video.preload = "metadata";
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("error", handleError);
    video.src = api.getAssetUrl(projectId, sourceVideoPath);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("error", handleError);
      video.removeAttribute("src");
    };
  }, [
    featurePreEligible,
    hasVideo,
    projectId,
    sourceVideoPath,
    suppliedDimensions,
  ]);

  useEffect(() => {
    setSelectedTarget((current) =>
      current && eligibleTargets.includes(current) ? current : (defaultTarget ?? null),
    );
  }, [defaultTarget, eligibleTargets]);

  useEffect(() => {
    if (!available || !hasVideo || isBusy || eligibleTargets.length === 0) {
      setIsOpen(false);
    }
  }, [available, eligibleTargets.length, hasVideo, isBusy]);

  useEffect(() => {
    if (!unavailableReason) return;
    upscaleVideoLog({
      event: "upscale-video:hd-disabled",
      feature: "upscale-video",
      concern: "client",
      project_id: projectId,
      canvas_id: canvasId,
      source_node_id: nodeId,
      reason: unavailableReason,
      source_width: sourceWidth ?? null,
      source_height: sourceHeight ?? null,
    });
  }, [canvasId, nodeId, projectId, sourceHeight, sourceWidth, unavailableReason]);

  useEffect(() => {
    if (!hideTerminallyUnavailable) return;
    videoToolbarReferenceLog({
      event: "video-toolbar-hd-visibility-resolved",
      feature: "video-node-toolbar-reference",
      trace_id: `video-node-toolbar-reference:${nodeId}`,
      node_id: nodeId,
      has_video: hasVideo,
      unavailable_reason: unavailableReason,
      terminally_unavailable: terminallyUnavailable,
      is_busy: isBusy,
      is_submitting: isSubmitting,
      hidden: hideAction,
    });
  }, [
    hasVideo,
    hideAction,
    hideTerminallyUnavailable,
    isBusy,
    isSubmitting,
    nodeId,
    terminallyUnavailable,
    unavailableReason,
  ]);

  useEffect(() => {
    if (
      isOpen ||
      isSubmitting ||
      !shouldRestoreFocusRef.current
    ) {
      return;
    }

    shouldRestoreFocusRef.current = false;
    const trigger = triggerRef.current;
    if (trigger && !trigger.disabled) trigger.focus();
  }, [isOpen, isSubmitting]);

  const close = useCallback(() => {
    if (isSubmitting) return;
    shouldRestoreFocusRef.current = true;
    setIsOpen(false);
  }, [isSubmitting]);

  const open = useCallback(() => {
    setSelectedTarget(defaultTarget ?? null);
    setIsOpen(true);
  }, [defaultTarget]);

  const confirm = useCallback(() => {
    if (
      !projectId ||
      !available ||
      !hasVideo ||
      isBusy ||
      isSubmitting ||
      !selectedTarget ||
      !eligibleTargets.includes(selectedTarget)
    ) {
      return;
    }
    const trimmedName =
      sourceName?.trim() || t("upscaleVideo.defaultSourceName");
    const outputName = `${trimmedName} - upscale`;
    const traceId = `upscale-video:${nodeId}`;
    setIsSubmitting(true);
    upscaleVideoLog({
      event: "upscale-video:confirm",
      feature: "upscale-video",
      concern: "client",
      trace_id: traceId,
      project_id: projectId,
      canvas_id: canvasId,
      source_node_id: nodeId,
      target_resolution: selectedTarget,
    });
    void api
      .createUpscaleVideo(projectId, nodeId, canvasId, outputName, selectedTarget)
      .then(({ nodeId: createdNodeId }) => {
        upscaleVideoLog({
          event: "upscale-video:create-scheduled",
          feature: "upscale-video",
          concern: "client",
          trace_id: traceId,
          project_id: projectId,
          canvas_id: canvasId,
          source_node_id: nodeId,
          upscale_node_id: createdNodeId,
          target_resolution: selectedTarget,
        });
        shouldRestoreFocusRef.current = true;
        setIsOpen(false);
        toast.success(t("upscaleVideo.createStarted"));
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : String(error);
        upscaleVideoLog({
          event: "upscale-video:create-failed",
          feature: "upscale-video",
          concern: "client",
          trace_id: traceId,
          project_id: projectId,
          canvas_id: canvasId,
          source_node_id: nodeId,
          reason: message,
        });
        toast.error(t("upscaleVideo.createFailed", { message }));
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [
    available,
    canvasId,
    hasVideo,
    eligibleTargets,
    isBusy,
    isSubmitting,
    nodeId,
    projectId,
    sourceName,
    selectedTarget,
    t,
  ]);

  const action = useMemo<ToolbarAction>(
    () => ({
      key: "upscale-hd",
      label: t("upscaleVideo.hdAction"),
      icon: isSubmitting ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : hdIconVariant === "badge" ? (
        <Hd aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
      ) : (
        <HdGlyph />
      ),
      onClick: open,
      disabled: !hasVideo || isBusy || isSubmitting || !selectedTarget,
      disabledReason: unavailableReason
        ? t(UNAVAILABLE_REASON_I18N_KEY[unavailableReason])
        : undefined,
      hidden: hideAction,
      triggerRef,
      ariaHasPopup: "dialog",
      ariaExpanded: isOpen,
      ariaControls: `${titleId}-dialog`,
    }),
    [
      hasVideo,
      hdIconVariant,
      hideAction,
      isBusy,
      isOpen,
      isSubmitting,
      open,
      selectedTarget,
      t,
      titleId,
      unavailableReason,
    ],
  );

  const flyout = (
    <FlyoutPortal
      triggerRef={triggerRef}
      isOpen={isOpen}
      onClose={close}
      preferredPlacement="above"
      testId="upscale-video-flyout-portal"
    >
      {selectedTarget ? (
        <UpscaleVideoFlyout
          eligibleTargets={eligibleTargets}
          isSubmitting={isSubmitting}
          onCancel={close}
          onConfirm={confirm}
          onTargetChange={setSelectedTarget}
          selectedTarget={selectedTarget}
          titleId={titleId}
        />
      ) : null}
    </FlyoutPortal>
  );

  return { action, flyout };
}
